import {
  chatExampleCatalog,
  type ChatExample,
  type ChatExampleCategory,
} from "./chatExampleCatalog";

// Strip the *highlight* markers for the value inserted into the composer.
export function plainPrompt(prompt: string): string {
  return prompt.replace(/\*/g, "");
}

// Split a prompt into runs of plain and highlighted (product-name) text.
export function promptSegments(
  prompt: string
): { text: string; bold: boolean }[] {
  return prompt
    .split(/\*([^*]+)\*/)
    .map((text, index) => ({ text, bold: index % 2 === 1 }))
    .filter((segment) => segment.text.length > 0);
}

export function shuffle<T>(items: readonly T[], seed: string): T[] {
  let hash = 0;
  for (const char of seed)
    hash = (Math.imul(31, hash) + char.charCodeAt(0)) | 0;
  const result = [...items];
  // Seeded Fisher–Yates gives each chat a fresh mix that stays put afterward.
  for (let i = result.length - 1; i > 0; i--) {
    hash = (hash + 0x6d2b79f5) | 0;
    let random = Math.imul(hash ^ (hash >>> 15), 1 | hash);
    random =
      (random + Math.imul(random ^ (random >>> 7), 61 | random)) ^ random;
    const j = Math.floor(
      (((random ^ (random >>> 14)) >>> 0) / 4294967296) * (i + 1)
    );
    [result[i], result[j]] = [result[j]!, result[i]!];
  }
  return result;
}

// Prefer fully available connector combinations, then partial matches, general
// suggestions, and popular services. Shuffle each tier using the chat ID so
// new chats draw from the full bank and existing chats keep their selections.
export function pickChatExamples(
  connectorIdentifiers: readonly string[],
  count: number,
  seed: string,
  category: ChatExampleCategory = "tasks"
): ChatExample[] {
  const userConnectors = new Set(connectorIdentifiers);
  const tiers: ChatExample[][] = [[], [], [], []];
  for (const example of chatExampleCatalog[category]) {
    const matches = example.connectors.filter((c) => userConnectors.has(c));
    const tier =
      example.connectors.length === 0
        ? 2
        : matches.length === example.connectors.length
          ? 0
          : matches.length > 0
            ? 1
            : 3;
    tiers[tier]!.push(example);
  }
  const pool = tiers.flatMap((tier) => shuffle(tier, `${seed}:${category}`));

  const picked: ChatExample[] = [];
  const usedLeads = new Set<string>();
  for (const example of pool) {
    if (picked.length >= count) break;
    const lead = example.connectors[0];
    if (lead !== undefined && usedLeads.has(lead)) continue;
    if (lead !== undefined) usedLeads.add(lead);
    picked.push(example);
  }
  return picked;
}
